import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Icon from 'react-native-vector-icons/MaterialCommunityIcons'
import { useNavigate } from 'react-router-dom'

import Dialog from '../../comps/Dialog/Dialog'
import { auth } from '../../fire'
//@ts-ignore
import styles from './ItemPrices.module.css'

interface PriceColumn {
  readonly stateKey: string
  readonly label: string
  readonly cityIndex?: number
  readonly list: readonly c.Price[]
}

interface PendingChange {
  readonly itemLabel: string
  readonly stateKey: string
  readonly columnLabel: string
  readonly oldValue: number | string | undefined
  readonly newValue: string
  readonly cityIndex?: number
}

interface NewRow {
  readonly id: string
  label: string
  prices: Record<string, string>
}

export default React.memo(function ItemPrices() {
  const navigate = useNavigate()
  const admins = r.useEnv<c.Dict<boolean>>('admins')
  const isAdmin = !!admins?.[auth.currentUser?.uid ?? '']

  const [saleTag, setSaleTag] = React.useState('')

  React.useEffect(() => {
    c.dispatch(c.efficiencyPricesAction())
  }, [])

  const allPrices = c.useSelector(c.selectEfficiencyPrices)
  const saleTags = React.useMemo(
    () =>
      Object.keys(allPrices || {}).filter((tag) =>
        c.rowableFields.some((field) => field.startsWith(tag)),
      ),
    [allPrices],
  )

  const items = r.useEnv<c.Opts>(saleTag) || c.emptyOpts

  const salePrices = c.useSelector(c.selectEfficiencyPrice(saleTag))

  const columns = React.useMemo((): PriceColumn[] => {
    if (!salePrices) return []
    const cols: PriceColumn[] = []
    for (const [stateKey, stateData] of Object.entries(salePrices)) {
      const data = stateData as any
      if (data?.pricesByCity) {
        for (let i = 0; i < data.pricesByCity.length; i++) {
          const city = data.pricesByCity[i]
          cols.push({
            stateKey,
            label: city.label
              ? `${stateKey} — ${city.label as string}`
              : stateKey,
            cityIndex: i,
            list: city.list || [],
          })
        }
      } else if (data?.list) {
        cols.push({
          stateKey,
          label: (data.label as string) || stateKey,
          list: data.list,
        })
      }
    }
    return cols
  }, [salePrices])

  const getPrice = React.useCallback(
    (itemValue: string, col: PriceColumn): number | string | undefined => {
      const found = col.list.find((p) => p.label === itemValue)
      return found?.value
    },
    [],
  )

  //#region editing
  const [editingCell, setEditingCell] = React.useState<string | null>(null)
  const [editValue, setEditValue] = React.useState('')
  const [pendingChanges, setPendingChanges] = React.useState<
    Record<string, PendingChange>
  >({})
  const [newRows, setNewRows] = React.useState<NewRow[]>([])
  const [showDialog, setShowDialog] = React.useState(false)
  const cancelEditRef = React.useRef(false)

  const applying = c.useSelector(c.selectItemPricesApplying)
  const prevApplyingRef = React.useRef(false)
  React.useEffect(() => {
    if (prevApplyingRef.current && !applying) {
      setPendingChanges({})
      setNewRows([])
      setShowDialog(false)
    }
    prevApplyingRef.current = applying
  }, [applying])

  React.useEffect(() => {
    setPendingChanges({})
    setNewRows([])
  }, [saleTag])

  const handleCellClick = React.useCallback(
    (itemValue: string, colIdx: number, col: PriceColumn) => {
      const key = `${itemValue}::${colIdx}`
      setEditingCell(key)
      const existing = pendingChanges[key]
      if (existing) {
        setEditValue(existing.newValue)
      } else {
        const cur = getPrice(itemValue, col)
        setEditValue(cur !== undefined ? String(cur) : '')
      }
    },
    [pendingChanges, getPrice],
  )

  const handleCellBlur = React.useCallback(
    (itemValue: string, colIdx: number, col: PriceColumn) => {
      if (cancelEditRef.current) {
        cancelEditRef.current = false
        setEditingCell(null)
        return
      }
      const key = `${itemValue}::${colIdx}`
      const currentPrice = getPrice(itemValue, col)
      const currentStr = currentPrice !== undefined ? String(currentPrice) : ''
      const trimmed = editValue.trim()

      if (trimmed !== '' && trimmed !== currentStr) {
        setPendingChanges((prev) => ({
          ...prev,
          [key]: {
            itemLabel: itemValue,
            stateKey: col.stateKey,
            columnLabel: col.label,
            oldValue: currentPrice,
            newValue: trimmed,
            cityIndex: col.cityIndex,
          },
        }))
      } else if (trimmed === currentStr || trimmed === '') {
        setPendingChanges((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
      }
      setEditingCell(null)
    },
    [editValue, getPrice],
  )

  const handleKeyDown = React.useCallback(
    (
      e: React.KeyboardEvent,
      _itemValue: string,
      _colIdx: number,
      _col: PriceColumn,
    ) => {
      if (e.key === 'Enter') {
        ;(e.target as HTMLInputElement).blur()
      }
      if (e.key === 'Escape') {
        cancelEditRef.current = true
        ;(e.target as HTMLInputElement).blur()
      }
    },
    [],
  )

  const handleAddRow = React.useCallback(() => {
    setNewRows((prev) => [
      ...prev,
      { id: String(Date.now()), label: '', prices: {} },
    ])
  }, [])

  const handleNewRowLabel = React.useCallback(
    (rowIdx: number, value: string) => {
      setNewRows((prev) => {
        const next = [...prev]
        next[rowIdx] = { ...next[rowIdx]!, label: value }
        return next
      })
    },
    [],
  )

  const handleNewRowPrice = React.useCallback(
    (rowIdx: number, colIdx: number, value: string) => {
      setNewRows((prev) => {
        const next = [...prev]
        next[rowIdx] = {
          ...next[rowIdx]!,
          prices: { ...next[rowIdx]!.prices, [colIdx]: value },
        }
        return next
      })
    },
    [],
  )
  const handleDeleteRow = React.useCallback(
    (item: c.Opt) => {
      if (!window.confirm(`Delete "${item.label}"?`)) return
      c.dispatch(c.deleteItem({ saleTag, itemValue: item.value }))
    },
    [saleTag],
  )

  const handleMoveRow = React.useCallback(
    (idx: number, direction: -1 | 1) => {
      const target = idx + direction
      if (target < 0 || target >= items.length) return
      const reordered = [...items]
      const temp = reordered[idx]!
      reordered[idx] = reordered[target]!
      reordered[target] = temp
      c.dispatch(c.reorderItems({ saleTag, items: reordered }))
    },
    [items, saleTag],
  )
  //#endregion editing

  //#region apply
  const changesList = React.useMemo(
    () => Object.values(pendingChanges),
    [pendingChanges],
  )

  const newRowChanges = React.useMemo(
    () =>
      newRows
        .filter((row) => row.label)
        .flatMap((row) =>
          Object.entries(row.prices)
            .filter(([, p]) => p !== '')
            .map(([colIdx, price]) => ({
              itemLabel: row.label,
              columnLabel: columns[parseInt(colIdx)]?.label || '',
              stateKey: columns[parseInt(colIdx)]?.stateKey || '',
              cityIndex: columns[parseInt(colIdx)]?.cityIndex,
              oldValue: undefined as number | string | undefined,
              newValue: price,
            })),
        ),
    [newRows, columns],
  )

  const totalChanges = changesList.length + newRowChanges.length
  const hasChanges = totalChanges > 0

  const handleApply = React.useCallback(() => {
    setShowDialog(true)
  }, [])

  const handleFinalApply = React.useCallback(() => {
    const changes: c.ItemPriceChange[] = changesList.map((ch) => ({
      saleTag,
      stateKey: ch.stateKey,
      itemLabel: ch.itemLabel,
      oldValue: ch.oldValue ?? '',
      newValue: parseFloat(ch.newValue) || 0,
      cityIndex: ch.cityIndex,
    }))

    for (const entry of newRowChanges) {
      changes.push({
        saleTag,
        stateKey: entry.stateKey,
        itemLabel: entry.itemLabel,
        oldValue: '',
        newValue: parseFloat(entry.newValue) || 0,
        cityIndex: entry.cityIndex,
      })
    }

    const newItemEntries = newRows
      .filter((row) => row.label)
      .map((row) => ({ label: row.label, value: row.label }))

    c.dispatch(
      c.applyItemPriceChanges({
        changes,
        itemsEnvKey: saleTag || undefined,
        newItems: newItemEntries.length > 0 ? newItemEntries : undefined,
      }),
    )
  }, [changesList, newRowChanges, newRows, saleTag])

  const handleDialogClose = React.useCallback(() => {
    setShowDialog(false)
  }, [])
  //#endregion apply

  const showTable = saleTag && items.length > 0 && columns.length > 0

  if (!isAdmin) return null

  return (
    <div className={styles['root']}>
      <div className={styles['back-btn']} onClick={() => navigate(-1)}>
        <Icon color="rgba(128,128,128,0.8)" name="arrow-left" size={20} />
        <span>Back</span>
      </div>

      <div className={styles['warning']}>
        Do not modify &quot;Mini Split&quot; and &quot;Windows&quot; for now
      </div>

      <div className={styles['inputs']}>
        <div className={styles['input-group']}>
          <label className={styles['label']}>Section</label>
          <select
            className={styles['select']}
            onChange={(e) => setSaleTag(e.target.value)}
            value={saleTag}
          >
            <option value="">Select...</option>
            {saleTags.map((tag) => (
              <option key={tag} value={tag}>
                {c.getFieldLabel(tag as c.CustomerField)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {hasChanges && !showDialog && (
        <div className={styles['sticky-bar']}>
          <span className={styles['changes-text']}>
            {totalChanges} unsaved change
            {totalChanges !== 1 ? 's' : ''}
          </span>
          <button
            className={styles['apply-btn']}
            disabled={applying}
            onClick={handleApply}
          >
            {applying ? 'Applying...' : 'Review & Apply'}
          </button>
        </div>
      )}

      {showTable ? (
        <>
          <div className={styles['table-wrapper']}>
            <table className={styles['table']}>
              <thead>
                <tr>
                  <th className={styles['th-delete']} />
                  <th className={styles['th-sort']} />
                  <th className={styles['th-name']}>Item</th>
                  {columns.map((col, i) => (
                    <th className={styles['th']} key={i}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item, itemIdx) => (
                  <tr key={item.value}>
                    <td className={styles['td-delete']}>
                      <div
                        className={styles['delete-btn']}
                        onClick={() => handleDeleteRow(item)}
                      >
                        <Icon
                          color="rgba(180,50,50,0.6)"
                          name="trash-can-outline"
                          size={16}
                        />
                      </div>
                    </td>
                    <td className={styles['td-sort']}>
                      <div className={styles['sort-btns']}>
                        <div
                          className={
                            itemIdx === 0
                              ? styles['sort-arrow-disabled']
                              : styles['sort-arrow']
                          }
                          onClick={() => handleMoveRow(itemIdx, -1)}
                        >
                          <Icon
                            color="rgba(128,128,128,0.7)"
                            name="chevron-up"
                            size={16}
                          />
                        </div>
                        <div
                          className={
                            itemIdx === items.length - 1
                              ? styles['sort-arrow-disabled']
                              : styles['sort-arrow']
                          }
                          onClick={() => handleMoveRow(itemIdx, 1)}
                        >
                          <Icon
                            color="rgba(128,128,128,0.7)"
                            name="chevron-down"
                            size={16}
                          />
                        </div>
                      </div>
                    </td>
                    <td className={styles['td-name']}>{item.label}</td>
                    {columns.map((col, colIdx) => {
                      const key = `${item.value}::${colIdx}`
                      const isEditing = editingCell === key
                      const change = pendingChanges[key]
                      const displayPrice = change
                        ? change.newValue
                        : getPrice(item.value, col)

                      return (
                        <td className={styles['td-price']} key={colIdx}>
                          {isEditing ? (
                            <input
                              autoFocus
                              className={styles['price-input']}
                              onBlur={() =>
                                handleCellBlur(item.value, colIdx, col)
                              }
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) =>
                                handleKeyDown(e, item.value, colIdx, col)
                              }
                              type="text"
                              value={editValue}
                            />
                          ) : (
                            <div
                              className={
                                change
                                  ? styles['price-changed']
                                  : styles['price-display']
                              }
                              onClick={() =>
                                handleCellClick(item.value, colIdx, col)
                              }
                            >
                              <span>
                                {displayPrice !== undefined &&
                                displayPrice !== ''
                                  ? typeof displayPrice === 'number'
                                    ? `$${displayPrice}`
                                    : displayPrice
                                  : '—'}
                              </span>
                              <span className={styles['pencil']}>
                                <Icon
                                  color="rgba(128,128,128,0.5)"
                                  name="pencil"
                                  size={12}
                                />
                              </span>
                            </div>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
                {newRows.map((row, rowIdx) => (
                  <tr key={`new-${row.id}`}>
                    <td className={styles['td-delete']} />
                    <td className={styles['td-sort']} />
                    <td className={styles['td-name']}>
                      <input
                        className={styles['name-input']}
                        onChange={(e) =>
                          handleNewRowLabel(rowIdx, e.target.value)
                        }
                        placeholder="New item name"
                        value={row.label}
                      />
                    </td>
                    {columns.map((_, colIdx) => (
                      <td className={styles['td-price']} key={colIdx}>
                        <input
                          className={styles['price-input']}
                          onChange={(e) =>
                            handleNewRowPrice(rowIdx, colIdx, e.target.value)
                          }
                          placeholder="—"
                          type="text"
                          value={row.prices[colIdx] || ''}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles['add-row']}>
            <div className={styles['add-btn']} onClick={handleAddRow}>
              <Icon color="rgba(128,128,128,0.8)" name="plus" size={20} />
            </div>
          </div>
        </>
      ) : (
        saleTag && (
          <div className={styles['empty-state']}>
            {items.length === 0
              ? `No items found at Misc/env/${saleTag}`
              : 'No price data found for the selected sale tag'}
          </div>
        )
      )}

      <Dialog
        onClose={handleDialogClose}
        open={showDialog}
        title="Review Price Changes"
      >
        <div className={styles['dialog-content']}>
          <table className={styles['summary-table']}>
            <thead>
              <tr>
                <th>Item</th>
                <th>State</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {changesList.map((ch, i) => (
                <tr key={i}>
                  <td>{ch.itemLabel}</td>
                  <td>{ch.columnLabel}</td>
                  <td className={styles['old-value']}>
                    {ch.oldValue !== undefined && ch.oldValue !== ''
                      ? `$${ch.oldValue}`
                      : '—'}
                  </td>
                  <td className={styles['new-value']}>${ch.newValue}</td>
                </tr>
              ))}
              {newRowChanges.map((entry, i) => (
                <tr key={`new-${i}`}>
                  <td>{entry.itemLabel}</td>
                  <td>{entry.columnLabel}</td>
                  <td className={styles['old-value']}>—</td>
                  <td className={styles['new-value']}>${entry.newValue}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles['dialog-actions']}>
            <button
              className={styles['apply-btn']}
              disabled={applying}
              onClick={handleFinalApply}
            >
              {applying ? 'Applying...' : 'Apply'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  )
})

export const rootSurfaceItemPrices: c.Surface = 'canvas'
